/**
 * FloatingResearchBubble - Global floating bubble for Agent Chat
 *
 * Shows the unified AI icon (from agents/shared/icon.svg) by default.
 * Morphing particles on hover. Colors adapt to the active agent's theme.
 * Positioned fixed in bottom-right corner of screen.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useResearchAgent } from '../context/ResearchAgentContext';
import { AGENT_CONFIG, AGENT_ICONS } from '../context/AgentChatContext';
import './FloatingResearchBubble.css';

const BUBBLE_PARTICLE_COUNT = 16;

function getBubbleShapePositions(shape) {
  const center = 20;

  switch (shape) {
    case 'constellation':
      return [
        { x: 20, y: 18 }, { x: 18, y: 22 }, { x: 22, y: 22 },
        { x: 16, y: 18 }, { x: 24, y: 18 }, { x: 20, y: 24 },
        { x: 12, y: 12 }, { x: 28, y: 12 }, { x: 12, y: 28 }, { x: 28, y: 28 },
        { x: 14, y: 20 }, { x: 26, y: 20 }, { x: 20, y: 10 }, { x: 20, y: 30 },
        { x: 16, y: 14 }, { x: 24, y: 26 }
      ];
    case 'mario':
      return [
        { x: 16, y: 12 }, { x: 20, y: 10 }, { x: 24, y: 12 },
        { x: 14, y: 14 }, { x: 26, y: 14 },
        { x: 16, y: 18 }, { x: 20, y: 16 }, { x: 24, y: 18 },
        { x: 18, y: 22 }, { x: 22, y: 22 },
        { x: 18, y: 28 }, { x: 22, y: 28 }, { x: 20, y: 26 },
        { x: 28, y: 16 }, { x: 30, y: 12 }, { x: 32, y: 10 }
      ];
    case 'samus':
      return Array.from({ length: BUBBLE_PARTICLE_COUNT }, (_, i) => {
        const angle = (i / BUBBLE_PARTICLE_COUNT) * Math.PI * 2;
        const r = i < 10 ? 10 : 5;
        return { x: center + Math.cos(angle) * r, y: center + Math.sin(angle) * r };
      });
    case 'ship':
      return [
        { x: 32, y: 20 }, { x: 28, y: 18 }, { x: 28, y: 22 },
        { x: 24, y: 20 }, { x: 20, y: 20 }, { x: 16, y: 20 },
        { x: 22, y: 14 }, { x: 18, y: 10 }, { x: 22, y: 26 }, { x: 18, y: 30 },
        { x: 12, y: 18 }, { x: 12, y: 22 }, { x: 8, y: 20 },
        { x: 6, y: 20 }, { x: 4, y: 18 }, { x: 4, y: 22 }
      ];
    default:
      return Array.from({ length: BUBBLE_PARTICLE_COUNT }, (_, i) => ({
        x: center + (Math.random() - 0.5) * 24,
        y: center + (Math.random() - 0.5) * 24
      }));
  }
}

function FloatingResearchBubble() {
  const { chatPanelOpen, openChat, activeAgent } = useResearchAgent();
  const [morphPhase, setMorphPhase] = useState('constellation');
  const [particles, setParticles] = useState(() =>
    getBubbleShapePositions('constellation').map((pos, i) => ({
      ...pos,
      id: i,
      size: 1.0 + Math.random() * 0.4,
      opacity: 0.6 + Math.random() * 0.4
    }))
  );
  const [targetPositions, setTargetPositions] = useState(() => getBubbleShapePositions('constellation'));
  const [isHovered, setIsHovered] = useState(false);
  const animationRef = useRef(null);
  const phaseTimeoutRef = useRef(null);

  const colors = AGENT_CONFIG[activeAgent]?.colors || AGENT_CONFIG.research.colors;
  const iconDataUrl = AGENT_ICONS.shared;

  // Run morphing animation only when hovered
  useEffect(() => {
    if (!isHovered) {
      if (phaseTimeoutRef.current) clearTimeout(phaseTimeoutRef.current);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      setMorphPhase('constellation');
      setTargetPositions(getBubbleShapePositions('constellation'));
      return;
    }

    const phases = ['constellation', 'mario', 'samus', 'ship'];
    const durations = [2500, 2500, 2500, 2500];
    let currentIndex = 0;

    const cyclePhase = () => {
      currentIndex = (currentIndex + 1) % phases.length;
      const nextPhase = phases[currentIndex];
      setMorphPhase(nextPhase);
      setTargetPositions(getBubbleShapePositions(nextPhase));
      phaseTimeoutRef.current = setTimeout(cyclePhase, durations[currentIndex]);
    };

    phaseTimeoutRef.current = setTimeout(cyclePhase, durations[0]);

    return () => {
      if (phaseTimeoutRef.current) clearTimeout(phaseTimeoutRef.current);
    };
  }, [isHovered]);

  // Continuous particle animation only when hovered
  useEffect(() => {
    if (!isHovered) {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      return;
    }

    let lastTime = performance.now();

    const animate = (currentTime) => {
      const deltaTime = (currentTime - lastTime) / 1000;
      lastTime = currentTime;

      setParticles(prev => prev.map((particle, i) => {
        const target = targetPositions[i] || { x: 20, y: 20 };
        const distX = target.x - particle.x;
        const distY = target.y - particle.y;
        const distance = Math.sqrt(distX * distX + distY * distY);

        const easing = distance > 3 ? 4.5 : 2.0;
        const dx = distX * easing * deltaTime;
        const dy = distY * easing * deltaTime;

        const wobbleAmount = distance < 2 ? 0.25 : 0.08;
        const wobbleX = Math.sin(currentTime * 0.002 + i * 0.8) * wobbleAmount;
        const wobbleY = Math.cos(currentTime * 0.0018 + i * 1.3) * wobbleAmount;

        const sizePulse = 1 + Math.sin(currentTime * 0.003 + i * 0.5) * 0.15;

        return {
          ...particle,
          x: particle.x + dx + wobbleX,
          y: particle.y + dy + wobbleY,
          size: (0.9 + (i % 3) * 0.15) * sizePulse,
          opacity: 0.7 + Math.sin(currentTime * 0.002 + i * 0.3) * 0.25
        };
      }));

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isHovered, targetPositions]);

  // Don't render when chat panel is open
  if (chatPanelOpen) return null;

  return (
    <button
      className={`floating-research-bubble ${isHovered ? 'hovered' : ''}`}
      onClick={() => openChat()}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {isHovered ? (
        // Animated morphing particles on hover
        <svg viewBox="0 0 40 40" className={`floating-avatar-svg morphing morph-${morphPhase}`}>
          <defs>
            <linearGradient id="floatingBubbleGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={colors.bubble} />
              <stop offset="100%" stopColor={colors.bubbleEnd} />
            </linearGradient>
            <filter id="floatingBubbleGlow">
              <feGaussianBlur stdDeviation="0.6" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <radialGradient id="floatingCenterGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="white" stopOpacity="0.2" />
              <stop offset="100%" stopColor="white" stopOpacity="0" />
            </radialGradient>
          </defs>
          <rect x="2" y="2" width="36" height="36" rx="10" ry="10" fill="url(#floatingBubbleGrad)" />
          <rect x="8" y="8" width="24" height="24" rx="6" ry="6" fill="url(#floatingCenterGlow)" className="center-glow" />
          <g className="particle-field">
            {particles.map((p) => (
              <circle
                key={p.id}
                cx={p.x}
                cy={p.y}
                r={p.size}
                fill="white"
                opacity={p.opacity}
                filter="url(#floatingBubbleGlow)"
              />
            ))}
          </g>
          <g className="particle-connections" opacity="0.15">
            {particles.slice(0, 8).map((p, i) => {
              const next = particles[(i + 1) % 8];
              const dist = Math.hypot(next.x - p.x, next.y - p.y);
              if (dist < 12) {
                return (
                  <line
                    key={`conn-${i}`}
                    x1={p.x}
                    y1={p.y}
                    x2={next.x}
                    y2={next.y}
                    stroke="white"
                    strokeWidth="0.3"
                  />
                );
              }
              return null;
            })}
          </g>
        </svg>
      ) : (
        // Static unified AI icon
        <svg viewBox="0 0 40 40" className="floating-avatar-svg static">
          <defs>
            <linearGradient id="floatingBubbleGradStatic" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={colors.bubble} />
              <stop offset="100%" stopColor={colors.bubbleEnd} />
            </linearGradient>
          </defs>
          <rect x="2" y="2" width="36" height="36" rx="10" ry="10" fill="url(#floatingBubbleGradStatic)" />
          {iconDataUrl ? (
            <image href={iconDataUrl} x="2" y="2" width="36" height="36" />
          ) : (
            <circle cx="20" cy="20" r="4" fill="white" fillOpacity="0.9" />
          )}
        </svg>
      )}
      <span className="floating-bubble-tooltip" style={{ background: colors.bubble, '--tooltip-bg': colors.bubble }}>
        {AGENT_CONFIG[activeAgent]?.name || 'Agent Chat'}
      </span>
    </button>
  );
}

export default FloatingResearchBubble;
